import { Filters } from '../components/FiltersSiderbar/Filters'

export interface RequiredDataTypes {
  jobListings: boolean
  recruiters: boolean
  tags: boolean
}

interface PresetFilter {
  selectedValues?: string[]
}

interface PresetFilters {
  jobListings?: PresetFilter
  recruiters?: PresetFilter
  tags?: PresetFilter
}

/**
 * Determines what data types are required based on the current filters
 * @param filters - The current filter state
 * @returns Object indicating which data types need to be loaded
 */
export const getRequiredDataTypes = (filters: Filters): RequiredDataTypes => {
  const required: RequiredDataTypes = {
    jobListings: false,
    recruiters: false,
    tags: false,
  }

  if (!filters) {
    return required
  }

  // Check if any filters require job listings data
  if (
    filters.jobListings?.selectedValues &&
    filters.jobListings.selectedValues.length > 0
  ) {
    required.jobListings = true
  }

  // Check if any filters require recruiters data
  if (
    filters.recruiters?.selectedValues &&
    filters.recruiters.selectedValues.length > 0
  ) {
    required.recruiters = true
  }

  // Check if any filters require tags data
  if (filters.tags?.selectedValues && filters.tags.selectedValues.length > 0) {
    required.tags = true
  }
  return required
}

/**
 * Determines what data types are required based on a preset's filters
 * @param presetFilters - The preset's filter configuration
 * @returns Object indicating which data types need to be loaded
 */
export const getRequiredDataTypesFromPreset = (
  presetFilters: PresetFilters
): RequiredDataTypes => {
  const required: RequiredDataTypes = {
    jobListings: false,
    recruiters: false,
    tags: false,
  }

  if (!presetFilters) {
    return required
  }

  // Check if preset has job listings filter with selected values
  if (
    presetFilters.jobListings &&
    presetFilters.jobListings.selectedValues?.length > 0
  ) {
    required.jobListings = true
  }

  // Check if preset has recruiters filter with selected values
  if (
    presetFilters.recruiters &&
    presetFilters.recruiters.selectedValues?.length > 0
  ) {
    required.recruiters = true
  }

  // Check if preset has tags filter with selected values
  if (presetFilters.tags && presetFilters.tags.selectedValues?.length > 0) {
    required.tags = true
  }

  return required
}

/**
 * Determines what data types are required for default presets
 * @param presetName - The name of the default preset
 * @returns Object indicating which data types need to be loaded
 */
export const getRequiredDataTypesForDefaultPreset = (
  _presetName: string
): RequiredDataTypes => {
  // Default presets typically don't require additional data loading
  // as they only use hardcoded hiring stage filters
  return {
    jobListings: false,
    recruiters: false,
    tags: false,
  }
}

/**
 * Checks if any of the required data types are missing from the loaded options
 * @param required - The required data types
 * @param loadedOptions - The currently loaded options
 * @returns Array of missing data types
 */
export const getMissingDataTypes = (
  required: RequiredDataTypes,
  loadedOptions: {
    jobListings: unknown[]
    recruiters: unknown[]
    tags: unknown[]
  }
): Array<keyof RequiredDataTypes> => {
  const missing: Array<keyof RequiredDataTypes> = []

  if (required.jobListings && loadedOptions.jobListings.length === 0) {
    missing.push('jobListings')
  }

  if (required.recruiters && loadedOptions.recruiters.length === 0) {
    missing.push('recruiters')
  }

  if (required.tags && loadedOptions.tags.length === 0) {
    missing.push('tags')
  }

  return missing
}

/**
 * Determines what data types are required based on URL parameters
 * @returns Object indicating which data types need to be loaded from URL
 */
export const getRequiredDataTypesFromUrl = (): RequiredDataTypes => {
  const searchParams = new URLSearchParams(window.location.search)

  const result = {
    // Check for jobListings filter in URL (also check legacy jobListingId param)
    jobListings:
      searchParams.has('jobListings') || searchParams.has('jobListingId'),
    // Check for recruiters filter in URL
    recruiters: searchParams.has('recruiters'),
    // Check for tags filter in URL
    tags: searchParams.has('tags'),
  }

  return result
}
